// @flow
import React from 'react';
import type { ChildrenArray } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import { mathStringChecker } from '../../types.js';
import { usePrintMode } from '../../jsx-activity/usePrintMode.js';
import { ActiveSectionContext } from './section-navigation.js';
import { GraduateCapIcon, HandToolsIcon } from './icons.js';
import { InternalCallOutList } from './InternalCallOutList.js';
import { InternalHeading } from '../../InternalHeading.js';
import { INVInformation } from './INVInformation.js';
import { INVProcedure } from './INVProcedure.js';
import {
  INVInvestigateQuestions,
  INVDiscussionQuestions,
} from '../common-components/questions.js';
import { INVDownloadableAssets } from '../common-components/INVDownloadableAssets.js';
import { Text } from '../../Text.js';
import type { MathString } from '../../types.js';

const pageContentsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 40,
  marginTop: 24,
};

// We always want React to render the entire subtree even if we aren't displaying the
// content to the user, as our validation is tied to the component lifecycle. If we hide
// the content from the user by simply not rendering it, no validation will occur for that
// subtree. This style object is used so that we always render the subtree, but don't
// visually display the contents to the user.
const hideContentStyle = {
  visibility: 'hidden',
  position: 'fixed',
  bottom: '100vh',
};

// TODO make this show/hide pattern first class, probably by
// implementing a ShowContent component at the section-navigation
// level than applies the hidden content style as appropriate.

export function INVOverview(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVOverviewIntroduction>
    | React$Element<typeof INVObjectives>
    | React$Element<typeof INVMaterials>,
  >,
  _sectionIndex: number,
  ...CompilerInjectedProps,
|}) {
  const { children, _sectionIndex } = props;

  const inPrintMode = usePrintMode();
  const activeSection = React.useContext(ActiveSectionContext);
  const isActive =
    // this is required to have the section render out of context in the docs
    _sectionIndex !== undefined ? _sectionIndex === activeSection : true;

  const { failedValidation } = usePropsValidation(props, INVOverview);
  if (failedValidation) return null;

  return (
    <section
      style={
        isActive || inPrintMode ? { breakBefore: 'page' } : hideContentStyle
      }
    >
      <div style={pageContentsStyle}>{children}</div>
    </section>
  );
}
INVOverview.spec = {
  children: childrenArrayMatcher(
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVOverviewIntroduction),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVObjectives),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVMaterials),
    },
  ),
};

export function INVOverviewIntroduction(props: {|
  children: ChildrenArray<React$Element<typeof Text>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVOverviewIntroduction,
  );
  if (failedValidation) return null;

  return <div>{props.children}</div>;
}
INVOverviewIntroduction.spec = {
  children: t.childrenArray(t.elementType(Text)),
};

export function INVObjectives(props: {|
  children: ChildrenArray<React$Element<typeof INVObjective>>,
  ...CompilerInjectedProps,
|}) {
  const { children } = props;

  const { failedValidation } = usePropsValidation(props, INVObjectives);
  if (failedValidation) return null;

  return (
    <InternalCallOutList title="Objectives" icon={<GraduateCapIcon />}>
      {children}
    </InternalCallOutList>
  );
}
INVObjectives.spec = {
  children: childrenArrayMatcher({
    min: 1,
    max: Infinity,
    elementType: t.elementType(INVObjective),
  }),
};

export function INVObjective(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { children } = props;

  const { failedValidation } = usePropsValidation(props, INVObjective);
  if (failedValidation) return null;

  return children;
}
INVObjective.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVMaterials(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVMaterialsItem>
    | React$Element<typeof INVDownloadableAssets>,
  >,
  _graphicSelection: number,
  ...CompilerInjectedProps,
|}) {
  const { children } = props;

  const { failedValidation } = usePropsValidation(props, INVMaterials);

  if (failedValidation) return null;

  return (
    <InternalCallOutList title="Materials" icon={<HandToolsIcon />}>
      {children}
    </InternalCallOutList>
  );
}
INVMaterials.spec = {
  children: childrenArrayMatcher(
    { min: 0, max: Infinity, elementType: t.elementType(INVMaterialsItem) },
    { min: 0, max: 1, elementType: t.elementType(INVDownloadableAssets) },
  ),
};

export function INVMaterialsItem(props: {|
  children: string,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVMaterialsItem);

  if (failedValidation) return null;

  return props.children;
}
INVMaterialsItem.spec = {
  children: t.string,
};

export function INVActivitySection(props: {|
  title: string,
  children: ChildrenArray<
    | React$Element<typeof INVInformation>
    | React$Element<typeof INVProcedure>
    | React$Element<typeof INVInvestigateQuestions>,
  >,
  _sectionIndex: number,
  ...CompilerInjectedProps,
|}) {
  const { children, title, _sectionIndex } = props;
  const inPrintMode = usePrintMode();
  const activeSection = React.useContext(ActiveSectionContext);
  const isActive =
    // this is required to have the section render out of context in the docs
    _sectionIndex !== undefined ? _sectionIndex === activeSection : true;

  const { failedValidation } = usePropsValidation(props, INVActivitySection);
  if (failedValidation) return null;

  return (
    <section
      style={
        isActive || inPrintMode ? { breakBefore: 'page' } : hideContentStyle
      }
    >
      <InternalHeading level={2}>{title}</InternalHeading>
      <div style={pageContentsStyle}>{children}</div>
    </section>
  );
}
INVActivitySection.spec = {
  title: t.string,
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(INVInformation) },
    { min: 0, max: 1, elementType: t.elementType(INVProcedure) },
    { min: 0, max: 1, elementType: t.elementType(INVInvestigateQuestions) },
  ),
};

export function INVReflection(props: {|
  children: ChildrenArray<
    React$Element<typeof Text> | React$Element<typeof INVDiscussionQuestions>,
  >,
  _sectionIndex: number,
  ...CompilerInjectedProps,
|}) {
  const { children, _sectionIndex } = props;
  const inPrintMode = usePrintMode();
  const activeSection = React.useContext(ActiveSectionContext);
  const isActive =
    // this is required to have the section render out of context in the docs
    _sectionIndex !== undefined ? _sectionIndex === activeSection : true;

  const { failedValidation } = usePropsValidation(props, INVReflection);
  if (failedValidation) return null;

  return (
    <section
      style={
        isActive || inPrintMode ? { breakBefore: 'page' } : hideContentStyle
      }
    >
      <div style={pageContentsStyle}>{children}</div>
    </section>
  );
}
INVReflection.spec = {
  children: childrenArrayMatcher(
    { min: 0, max: Infinity, elementType: t.elementType(Text) },
    { min: 0, max: 1, elementType: t.elementType(INVDiscussionQuestions) },
  ),
};
